import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  axisBottom,
  axisLeft,
  scaleBand,
  scaleTime,
  select,
  timeFormat,
  zoom,
  zoomIdentity,
} from 'd3';

import {
  buildTimelineData,
  computeTimelineDomain,
  DATA_SOURCE_CONFIG,
  DAY_IN_MS,
  DataSource,
  EventMarker,
  Session,
  TimelineEventPoint,
  TimelinePoint,
} from './timelineUtils';
import { mockEvents, mockSessions } from './mocks/rowerJourneyTimeline';

type RowerJourneyTimelineProps = {
  sessions?: Session[];
  events?: EventMarker[];
  selectedSessionId?: string | null;
  selectedEventId?: string | null;
  onSelectSession?: (session: Session) => void;
  onSelectEvent?: (event: EventMarker) => void;
};

const EVENT_COLOR = '#FB923C';
const DEFAULT_SOURCES: DataSource[] = Object.keys(DATA_SOURCE_CONFIG) as DataSource[];

const parseSession = (session: Session) => ({
  ...session,
  startDate: new Date(session.startTime),
  endDate: new Date(session.endTime),
});

const RowerJourneyTimelineD3: React.FC<RowerJourneyTimelineProps> = ({
  sessions = mockSessions,
  events = mockEvents,
  selectedSessionId,
  selectedEventId,
  onSelectSession,
  onSelectEvent,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const [activeSources, setActiveSources] = useState<DataSource[]>(DEFAULT_SOURCES);
  const [showEvents, setShowEvents] = useState(true);

  const filteredSessions = useMemo(
    () => sessions.filter((session) => activeSources.includes(session.dataSource)),
    [sessions, activeSources]
  );

  const filteredEvents = useMemo(() => (showEvents ? events : []), [events, showEvents]);

  const timelineData = useMemo(
    () => buildTimelineData(filteredSessions, filteredEvents),
    [filteredSessions, filteredEvents]
  );

  useEffect(() => {
    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    if (!container) return;

    const width = container.clientWidth || 900;
    const height = 340;
    const margin = { top: 40, right: 32, bottom: 64, left: 128 };
    const plotHeight = height - margin.top - margin.bottom;

    const svg = select(container)
      .selectAll<SVGSVGElement, null>('svg')
      .data([null])
      .join('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('touch-action', 'none');

    svg.selectAll('*').remove();

    if (!timelineData.points.length && !timelineData.eventPoints.length) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#475467')
        .attr('font-size', 14)
        .text('No session timeline data available');
      return;
    }

    const [minDate, maxDate] = computeTimelineDomain(timelineData.points, timelineData.eventPoints);
    const paddingMs = 2 * DAY_IN_MS;

    const xScale = scaleTime()
      .domain([new Date(minDate.getTime() - paddingMs), new Date(maxDate.getTime() + paddingMs)])
      .range([margin.left, width - margin.right]);

    const sources = DEFAULT_SOURCES.filter((source) =>
      timelineData.points.some((point) => point.session.dataSource === source)
    );

    const includeEventsLane = showEvents && timelineData.eventPoints.length > 0;
    const yDomain: Array<'events' | DataSource> = includeEventsLane
      ? ['events', ...sources]
      : [...sources];

    const yScale = scaleBand<'events' | DataSource>()
      .domain(yDomain)
      .range([margin.top, height - margin.bottom])
      .paddingInner(0.4)
      .paddingOuter(0.3);

    const yBand = yScale.bandwidth();
    const stackSlots = Math.max(timelineData.maxStack, 1);
    const stackSpacing = yBand / (stackSlots + 1);

    const root = svg.append('g');

    const yAxis = axisLeft(yScale)
      .tickSize(0)
      .tickFormat((d) =>
        d === 'events' ? 'Events & Regattas' : (DATA_SOURCE_CONFIG[d as DataSource]?.label ?? d)
      );

    root
      .append('g')
      .attr('transform', `translate(${margin.left}, 0)`)
      .call(yAxis)
      .call((sel) => sel.selectAll('text').attr('font-size', 12).attr('fill', '#475467'))
      .call((sel) => sel.select('.domain').remove());

    root
      .append('g')
      .attr('class', 'timeline-y-grid')
      .selectAll('line')
      .data(yDomain)
      .enter()
      .append('line')
      .attr('x1', margin.left)
      .attr('x2', width - margin.right)
      .attr('y1', (d) => (yScale(d) ?? margin.top) + yBand / 2)
      .attr('y2', (d) => (yScale(d) ?? margin.top) + yBand / 2)
      .attr('stroke', '#E5E7EB')
      .attr('opacity', (d) => (d === 'events' ? 0.5 : 0.35));

    const xAxisGroup = root
      .append('g')
      .attr('transform', `translate(0, ${height - margin.bottom})`);
    const xGridGroup = root.append('g').attr('class', 'timeline-grid');

    const applyXAxis = (scale: typeof xScale) => {
      const axisGenerator = axisBottom<Date>(scale).ticks(10).tickFormat(timeFormat('%b %d'));

      xAxisGroup.call(axisGenerator);
      xAxisGroup
        .selectAll('text')
        .attr('font-size', 12)
        .attr('fill', '#475467')
        .attr('dy', '1.2em')
        .attr('transform', 'rotate(-25)');
      xAxisGroup.selectAll('line').attr('stroke', '#D0D5DD').attr('opacity', 0.5);

      xGridGroup.attr('transform', `translate(0, ${height - margin.bottom})`).call(
        axisBottom(scale)
          .ticks(10)
          .tickSize(-plotHeight)
          .tickFormat(() => '')
      );
      xGridGroup.selectAll('line').attr('stroke', '#E5E7EB').attr('opacity', 0.35);
      xGridGroup.selectAll('text').remove();
      xGridGroup.select('.domain').attr('stroke', '#D0D5DD').attr('opacity', 0.5);
    };

    applyXAxis(xScale);

    const formatDateLabel = timeFormat('%b %d, %Y');
    const formatTimeLabel = timeFormat('%H:%M');

    const showTooltip = (html: string, event: MouseEvent | FocusEvent, currentTarget: Element) => {
      if (!tooltip) return;
      tooltip.innerHTML = html;
      tooltip.style.opacity = '1';

      const offset = 12;
      const containerRect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      let clientX: number;
      let clientY: number;

      if ('clientX' in event && typeof event.clientX === 'number') {
        clientX = event.clientX;
        clientY = event.clientY;
      } else {
        const rect = currentTarget.getBoundingClientRect();
        clientX = rect.left + rect.width / 2;
        clientY = rect.top + rect.height / 2;
      }

      const relativeLeft = clientX - containerRect.left + offset;
      const relativeTop = clientY - containerRect.top + offset;

      tooltip.style.left = `${Math.min(
        containerRect.width - tooltipRect.width - offset,
        Math.max(offset, relativeLeft)
      )}px`;
      tooltip.style.top = `${Math.min(
        containerRect.height - tooltipRect.height - offset,
        Math.max(offset, relativeTop)
      )}px`;
    };

    const moveTooltip = (event: MouseEvent, currentTarget: Element) => {
      if (!tooltip || tooltip.style.opacity === '0') return;
      showTooltip(tooltip.innerHTML, event, currentTarget);
    };

    const hideTooltip = () => {
      if (tooltip) tooltip.style.opacity = '0';
    };

    const buildSessionTooltip = (point: TimelinePoint) => {
      const session = parseSession(point.session);
      const sourceLabel = DATA_SOURCE_CONFIG[session.dataSource]?.label ?? session.dataSource;
      return `
        <div class="font-semibold mb-1">${sourceLabel}</div>
        <div>${session.sessionType}</div>
        <div>${formatDateLabel(point.x)}</div>
        <div>${formatTimeLabel(session.startDate)} - ${formatTimeLabel(session.endDate)}</div>
        <div>Avg Power: ${Math.round(session.avgPower)} W</div>
        <div>Max Power: ${Math.round(session.maxPower)} W</div>
      `;
    };

    const buildEventTooltip = (point: TimelineEventPoint) => {
      const multiDay = point.end.getTime() !== point.start.getTime();
      const startLabel = formatDateLabel(point.start);
      const endLabel = formatDateLabel(point.end);
      const dateLine = multiDay ? `${startLabel} – ${endLabel}` : startLabel;
      return `
        <div class="font-semibold mb-1">${point.event.label}</div>
        <div>${dateLine}</div>
        <div class="text-xs text-rowing-gray-500">${point.event.category}</div>
      `;
    };

    const plotLayer = root.append('g');

    const sessionNodes = plotLayer
      .append('g')
      .attr('role', 'list')
      .attr('aria-label', 'Training sessions')
      .selectAll<SVGCircleElement, TimelinePoint>('circle')
      .data(timelineData.points, (d: any) => d.session.id)
      .enter()
      .append('circle')
      .attr('cx', (d) => xScale(d.x))
      .attr('cy', (d) => {
        const laneTop = yScale(d.session.dataSource) ?? margin.top;
        return laneTop + stackSpacing * d.stackIndex - stackSpacing / 2;
      })
      .attr('r', 6)
      .attr('fill', (d) => DATA_SOURCE_CONFIG[d.session.dataSource]?.color ?? '#1E40AF')
      .attr('stroke', (d) => (selectedSessionId === d.session.id ? '#111827' : 'transparent'))
      .attr('stroke-width', 2)
      .attr('opacity', (d) =>
        selectedSessionId && selectedSessionId !== d.session.id ? 0.35 : 0.9
      )
      .attr('role', 'listitem')
      .attr('tabindex', 0)
      .attr('aria-label', (d) => {
        const label = DATA_SOURCE_CONFIG[d.session.dataSource]?.label ?? d.session.dataSource;
        return `${label} session on ${formatDateLabel(d.x)}: ${d.session.sessionType}`;
      })
      .on('mouseenter', (event, d) => {
        select(event.currentTarget).attr('opacity', 1).attr('r', 7);
        showTooltip(buildSessionTooltip(d), event, event.currentTarget as Element);
      })
      .on('mousemove', (event) => moveTooltip(event as MouseEvent, event.currentTarget as Element))
      .on('mouseleave', (event, d) => {
        const faded = selectedSessionId && selectedSessionId !== d.session.id;
        select(event.currentTarget)
          .attr('opacity', faded ? 0.35 : 0.9)
          .attr('r', 6);
        hideTooltip();
      })
      .on('focus', (event, d) => {
        select(event.currentTarget).attr('opacity', 1).attr('r', 7);
        showTooltip(buildSessionTooltip(d), event, event.currentTarget as Element);
      })
      .on('blur', (event, d) => {
        const faded = selectedSessionId && selectedSessionId !== d.session.id;
        select(event.currentTarget)
          .attr('opacity', faded ? 0.35 : 0.9)
          .attr('r', 6);
        hideTooltip();
      })
      .on('keydown', (event, d) => {
        const keyboardEvent = event as KeyboardEvent;
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault();
          onSelectSession?.(d.session);
        }
      })
      .on('click', (_, d) => {
        onSelectSession?.(d.session);
      });

    let eventNodes: ReturnType<
      typeof plotLayer.selectAll<SVGRectElement, TimelineEventPoint>
    > | null = null;

    if (includeEventsLane) {
      eventNodes = plotLayer
        .append('g')
        .attr('role', 'list')
        .attr('aria-label', 'Team events')
        .selectAll<SVGRectElement, TimelineEventPoint>('rect')
        .data(timelineData.eventPoints, (d: any) => d.event.id)
        .enter()
        .append('rect')
        .attr('x', (d) => xScale(d.start) - 6)
        .attr('y', () => {
          const lane = yScale('events') ?? margin.top;
          return lane + yBand * 0.5 - 6;
        })
        .attr('width', (d) => {
          const endInclusive = new Date(d.end.getTime() + DAY_IN_MS);
          return Math.max(12, xScale(endInclusive) - xScale(d.start));
        })
        .attr('height', 12)
        .attr('fill', EVENT_COLOR)
        .attr('stroke', '#F97316')
        .attr('stroke-width', (d) => (selectedEventId === d.event.id ? 2 : 1))
        .attr('opacity', (d) => (selectedEventId && selectedEventId !== d.event.id ? 0.45 : 0.9))
        .attr('tabindex', 0)
        .attr('aria-label', (d) => {
          const start = formatDateLabel(d.start);
          const end = formatDateLabel(d.end);
          const range = d.end.getTime() !== d.start.getTime() ? `${start} – ${end}` : start;
          return `${d.event.category} ${range}: ${d.event.label}`;
        })
        .on('mouseenter', (event, d) => {
          select(event.currentTarget).attr('opacity', 1);
          showTooltip(buildEventTooltip(d), event, event.currentTarget as Element);
        })
        .on('mousemove', (event) =>
          moveTooltip(event as MouseEvent, event.currentTarget as Element)
        )
        .on('mouseleave', (event, d) => {
          const faded = selectedEventId && selectedEventId !== d.event.id;
          select(event.currentTarget).attr('opacity', faded ? 0.45 : 0.9);
          hideTooltip();
        })
        .on('focus', (event, d) => {
          select(event.currentTarget).attr('opacity', 1);
          showTooltip(buildEventTooltip(d), event, event.currentTarget as Element);
        })
        .on('blur', (event, d) => {
          const faded = selectedEventId && selectedEventId !== d.event.id;
          select(event.currentTarget).attr('opacity', faded ? 0.45 : 0.9);
          hideTooltip();
        })
        .on('keydown', (event, d) => {
          const keyboardEvent = event as KeyboardEvent;
          if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
            keyboardEvent.preventDefault();
            onSelectEvent?.(d.event);
          }
        })
        .on('click', (_, d) => {
          onSelectEvent?.(d.event);
        });
    }

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 20])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .extent([
        [margin.left, margin.top],
        [width - margin.right, height - margin.bottom],
      ])
      .on('zoom', (event) => {
        const newX = event.transform.rescaleX(xScale);
        applyXAxis(newX);

        // Constrain session circles to plot area
        sessionNodes.attr('cx', (d) => {
          const cx = newX(d.x);
          return Math.max(margin.left, Math.min(width - margin.right, cx));
        });

        if (eventNodes) {
          eventNodes
            .attr('x', (d) => {
              const x = newX(d.start) - 6;
              return Math.max(margin.left - 6, Math.min(width - margin.right - 6, x));
            })
            .attr('width', (d) => {
              const endInclusive = new Date(d.end.getTime() + DAY_IN_MS);
              const startX = newX(d.start);
              const endX = newX(endInclusive);

              // Constrain to visible area
              const constrainedStartX = Math.max(
                margin.left,
                Math.min(width - margin.right, startX)
              );
              const constrainedEndX = Math.max(margin.left, Math.min(width - margin.right, endX));

              return Math.max(12, constrainedEndX - constrainedStartX);
            });
        }
      });

    svg.call(zoomBehavior as any).call(zoomBehavior.transform, zoomIdentity);

    root
      .append('text')
      .attr('x', width / 2)
      .attr('y', height - 12)
      .attr('text-anchor', 'middle')
      .attr('fill', '#667085')
      .attr('font-size', 11)
      .text('Drag to pan · Scroll/Pinch to zoom');

    return () => {
      if (tooltip) tooltip.style.opacity = '0';
    };
  }, [
    timelineData,
    showEvents,
    selectedSessionId,
    selectedEventId,
    onSelectEvent,
    onSelectSession,
  ]);

  return (
    <div className="relative w-full h-full">
      <div className="sr-only" id="timeline-desc">
        Interactive timeline of rower sessions and events. Toggle sources or events with the legend
        buttons. Drag to pan, scroll to zoom.
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        {DEFAULT_SOURCES.map((source) => {
          const config = DATA_SOURCE_CONFIG[source];
          const isActive = activeSources.includes(source);
          const isLastActive = activeSources.length === 1 && isActive;
          return (
            <button
              key={source}
              type="button"
              onClick={() => {
                if (isLastActive) return; // Prevent disabling last source
                setActiveSources((prev) =>
                  prev.includes(source)
                    ? prev.filter((value) => value !== source)
                    : [...prev, source]
                );
              }}
              disabled={isLastActive}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-all duration-200 focus:outline-none focus:ring-2 focus-visible:ring-accent/50 focus:ring-offset-2 ${
                isActive
                  ? 'border-rowing-blue text-rowing-navy bg-white hover:shadow-sm'
                  : 'border-rowing-gray-200 text-rowing-gray-500 bg-rowing-gray-50 hover:bg-rowing-gray-100'
              } ${isLastActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
              title={isLastActive ? 'At least one data source must be active' : undefined}
            >
              <span
                className="h-2.5 w-2.5 rounded-full transition-opacity duration-200"
                style={{ backgroundColor: config.color, opacity: isActive ? 1 : 0.3 }}
              />
              {config.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowEvents((prev) => !prev)}
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-all duration-200 focus:outline-none focus:ring-2 focus-visible:ring-accent/50 focus:ring-offset-2 ${
            showEvents
              ? 'border-rowing-blue text-rowing-navy bg-white hover:shadow-sm'
              : 'border-rowing-gray-200 text-rowing-gray-500 bg-rowing-gray-50 hover:bg-rowing-gray-100'
          } cursor-pointer`}
        >
          <span
            className="h-2.5 w-2.5 rounded transition-opacity duration-200"
            style={{ backgroundColor: EVENT_COLOR, opacity: showEvents ? 1 : 0.3 }}
          />
          Events & Regattas
        </button>
      </div>

      <div ref={containerRef} className="w-full h-[340px]" aria-describedby="timeline-desc" />
      <div
        ref={tooltipRef}
        className="pointer-events-none absolute z-10 rounded-md border border-rowing-gray-200 bg-white px-3 py-2 text-xs text-rowing-gray-700 shadow-lg"
        style={{ opacity: 0 }}
      />
    </div>
  );
};

export default RowerJourneyTimelineD3;
